import React from "react";
import { GoollooperNav, GoollooperFooter } from "./GoollooperSite";

/**
 * Goollooper legal pages — goollooper.com.
 * Adapted 2026-11-04 from the approved ixee.ai legal text (same platform,
 * same substance), rebranded to Goollooper as the operating platform.
 */

const Page = ({ title, children }) => (
  <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
    <GoollooperNav />
    <main className="max-w-3xl mx-auto px-5 py-14">
      <h1
        className="text-3xl font-bold text-[#161A1D]"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {title}
      </h1>
      <div className="text-neutral-700 leading-relaxed mt-6 space-y-4">{children}</div>
    </main>
    <GoollooperFooter />
  </div>
);

const H = ({ children }) => (
  <h2 className="text-lg font-semibold text-[#161A1D] pt-4">{children}</h2>
);

export const GoollooperPrivacy = () => (
  <Page title="Privacy Policy">
    <p>
      Goollooper is a social productivity platform that helps you find and work with
      service providers, merchants, recruiters and other users. Ixee, the assistant
      inside Goollooper, runs on the Goollooper platform. This policy explains what
      we collect and why.
    </p>
    <H>What we collect</H>
    <p>
      Account and profile information you provide (name, contact details, profile
      details, and for providers: specialties, classifications and quality indicators);
      location information used to match you with nearby providers and listings; the
      content of your requests, chats, files and projects; and basic device and usage
      information needed to run and secure the service.
    </p>
    <H>How we use it</H>
    <p>
      To operate the platform: understanding your requests, matching you with best-fit
      providers and listings, scheduling, preparing quotes and documents, sending
      notifications, preventing fraud and abuse, and improving the service.
    </p>
    <H>What we share</H>
    <p>
      We share the information needed to carry out a request — for example, your task
      details with a provider you are matched with. We do not sell your personal
      information. Service providers who help us run Goollooper (hosting, payments,
      analytics) process data only on our instructions.
    </p>
    <H>Your choices</H>
    <p>
      You can view and update your profile in Settings, control permissions, and delete
      your account at any time from the app or the Delete account page on this site.
      Deleting your account removes your profile and personal data, subject to legal
      retention requirements.
    </p>
    <H>Data privacy & transparency</H>
    <p>
      We collect the minimum data needed to provide the service, protect it with
      access controls and encryption in transit, and are transparent about how it is
      used. If this policy changes materially, we will let you know in the app.
    </p>
    <p className="text-sm text-neutral-500">Last updated: November 4, 2026.</p>
  </Page>
);

export const GoollooperTerms = () => (
  <Page title="Terms of Service">
    <p>
      By using Goollooper you agree to these terms. Goollooper is a marketplace and
      assistant platform operated by Goollooper Inc.
    </p>
    <H>The service</H>
    <p>
      Goollooper helps users describe what they need and matches them with providers,
      merchants, recruiters and listings. Its assistant, Ixee, may prepare quotes,
      schedules and documents and send messages on your behalf within the permissions
      you grant. Actions with financial or external effect ask for your confirmation first.
    </p>
    <H>Providers are independent</H>
    <p>
      Providers on Goollooper are independent businesses and individuals, not
      Goollooper employees. Agreements for work are between the customer and the
      provider; the REQ/Invoice document generated in the app records the agreed
      scope and price.
    </p>
    <H>Accounts</H>
    <p>
      Keep your account information accurate and your credentials secure. You are
      responsible for activity under your account. Provider profiles must be truthful;
      quality indicators and classifications may be verified.
    </p>
    <H>Acceptable use</H>
    <p>
      Do not misuse the service: no fraud, harassment, illegal goods or services, or
      attempts to bypass the platform's safety and permission controls. Accounts that
      violate these terms may be suspended.
    </p>
    <H>Payments</H>
    <p>
      Where payments are offered, they are processed by third-party payment processors
      under their terms. Fees for optional services (such as advertising or voice-only
      mode) are shown before you commit.
    </p>
    <H>Liability</H>
    <p>
      The service is provided "as is." To the extent permitted by law, we are not
      liable for indirect or consequential damages, or for the acts of independent
      providers beyond our reasonable control.
    </p>
    <p className="text-sm text-neutral-500">Last updated: November 4, 2026.</p>
  </Page>
);

export const GoollooperDeleteAccount = () => (
  <Page title="Delete your account">
    <p>You can delete your Goollooper account and personal data in either of these ways:</p>
    <H>In the app</H>
    <p>
      Open Goollooper, go to Settings, and choose "Delete account." Follow the confirmation
      steps. Your profile, projects and personal data are removed, subject to legal
      retention requirements (for example, transaction records we must keep).
    </p>
    <H>Through support</H>
    <p>
      If you cannot access the app, contact support from inside the app on any device
      signed in to your account, and ask for account deletion. We will verify the
      request is yours before deleting.
    </p>
    <p>
      Deletion is permanent. Listings and pitches are removed, and your name is
      detached from past transactions where the law allows.
    </p>
  </Page>
);
